/*
 * <prism-media-gallery> — product media gallery with thumbnail navigation.
 *
 * Switches the active media when a thumbnail is chosen and follows variant
 * selection (subscribes to variantChange and activates variant.featured_media).
 * Layout-agnostic: it toggles an "active" class + aria-current and scrolls the
 * media into view, so the section CSS decides between a stacked or single view.
 *
 * Markup contract:
 *   <prism-media-gallery data-section="{{ section.id }}">
 *     <div data-gallery-viewport>
 *       <div class="prism-media" data-media-id="{{ media.id }}">…</div>
 *     </div>
 *     <button data-gallery-thumb data-media-id="{{ media.id }}">…</button>
 *   </prism-media-gallery>
 */
if (!customElements.get('prism-media-gallery')) {
  customElements.define(
    'prism-media-gallery',
    class PrismMediaGallery extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.getAttribute('data-section');
        this.viewport = this.querySelector('[data-gallery-viewport]');
        this.thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-gallery-thumb]'));

        this.onThumbClick = this.onThumbClick.bind(this);
        this.onThumbKeydown = this.onThumbKeydown.bind(this);
        this.thumbs.forEach((thumb) => {
          thumb.addEventListener('click', this.onThumbClick);
          thumb.addEventListener('keydown', this.onThumbKeydown);
        });

        if (window.PUB_SUB_EVENTS && typeof subscribe === 'function') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (!event || !event.data || event.data.sectionId !== this.sectionId) return;
            var variant = event.data.variant;
            if (variant && variant.featured_media) this.activate(String(variant.featured_media.id));
          });
        }
      }

      disconnectedCallback() {
        this.thumbs.forEach((thumb) => {
          thumb.removeEventListener('click', this.onThumbClick);
          thumb.removeEventListener('keydown', this.onThumbKeydown);
        });
        if (this.unsubscribe) this.unsubscribe();
      }

      onThumbClick(e) {
        var thumb = e.currentTarget;
        this.activate(thumb.getAttribute('data-media-id'));
      }

      onThumbKeydown(e) {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        var index = this.thumbs.indexOf(e.currentTarget);
        if (e.key === 'ArrowRight') index = index + 1 >= this.thumbs.length ? 0 : index + 1;
        else index = index - 1 < 0 ? this.thumbs.length - 1 : index - 1;
        var next = this.thumbs[index];
        next.focus();
        this.activate(next.getAttribute('data-media-id'));
      }

      activate(mediaId) {
        if (!mediaId || !this.viewport) return;
        var target = this.viewport.querySelector('[data-media-id="' + mediaId + '"]');
        if (!target) return;

        this.viewport.querySelectorAll('[data-media-id]').forEach(function (media) {
          media.classList.toggle('prism-media--active', media === target);
        });
        this.thumbs.forEach(function (thumb) {
          var isActive = thumb.getAttribute('data-media-id') === mediaId;
          thumb.classList.toggle('prism-gallery__thumb--active', isActive);
          if (isActive) thumb.setAttribute('aria-current', 'true');
          else thumb.removeAttribute('aria-current');
        });

        var reduced = window.PRISM && window.PRISM.prefersReducedMotion && window.PRISM.prefersReducedMotion();
        target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  );
}
